import { useCallback } from 'react';
import { cartService } from '../services/cartService';
import { cartAnimationService } from '../services/cartAnimationService';

/**
 * Custom hook to handle adding to cart with animation
 * @param {Object} params - Configuration parameters
 * @returns {Object} Functions and state to handle cart addition
 */
export const useAddToCart = (params) => {
  const {
    cartItems,
    dispatch,
    addToCart,
    axiosInstance,
    setProduct,
    setCode,
    setIsNavbarHidden,
    setIsPaused,
    setLoading,
    animProductRef,
    countItems,
    store
  } = params;

  /**
   * Helper function to add product to cart and cleanup
   */
  const addProductAndCleanup = useCallback((product, qty = 1) => {
    cartService.addProductToCart({
      cartItems,
      product,
      qty,
      dispatch,
      addToCart,
      axiosInstance
    });
    cartService.cleanupAfterAddToCart(setProduct, setCode);
  }, [cartItems, dispatch, addToCart, axiosInstance, setProduct, setCode]);

  /**
   * Main function for adding to cart with animation
   */
  const addToCartWithAnimation = useCallback(async (product, qty = 1) => {
    try {
      const validationResult = cartService.validateAddToCartParams({
        cartItems,
        product,
        dispatch,
        addToCart,
        axiosInstance
      });

      if (!validationResult.success) {
        console.error('Validation failed:', validationResult.message);
        return { success: false, error: validationResult.message };
      }

      const shouldAnimate = cartService.shouldAnimateAddToCart();
      
      if (!shouldAnimate) {
        addProductAndCleanup(product, qty);
        return { success: true, animated: false };
      }

      const animationData = cartService.prepareAnimationData(product);
      if (!animationData) {
        addProductAndCleanup(product, qty);
        return { success: true, animated: false };
      }

      cartAnimationService.toggleNavbar(setIsNavbarHidden, true);
      if (animProductRef?.current) {
        cartAnimationService.setupProductToCartAnimation(
          animationData.productImgId,
          animationData.cartAltId,
          animProductRef.current
        );
      }

      cartAnimationService.animateCartShake(
        animationData.footerCartId,
        () => {
          addProductAndCleanup(product, qty);
          cartService.handleScanPauseLogic(countItems, setIsPaused, store?.item_limit);
        }
      );

      return { success: true, animated: true };

    } catch (error) {
      console.error('Error in addToCartWithAnimation:', error);
      
      try {
        addProductAndCleanup(product, qty);
      } catch (fallbackError) {
        console.error('Fallback add to cart failed:', fallbackError);
        return { success: false, error: fallbackError.message };
      }
      
      return { success: true, animated: false, warning: 'Animation failed but product added' };
    }
  }, [
    cartItems,
    dispatch,
    addToCart,
    axiosInstance,
    setProduct,
    setCode,
    setIsNavbarHidden,
    setIsPaused,
    animProductRef,
    countItems,
    store,
    addProductAndCleanup
  ]);

  /**
   * Simplified function for adding to cart without animation
   */
  const addToCartDirect = useCallback((product, qty = 1) => {
    try {
      const validationResult = cartService.validateAddToCartParams({
        cartItems,
        product,
        dispatch,
        addToCart,
        axiosInstance
      });

      if (!validationResult.success) {
        console.error('Validation failed:', validationResult.message);
        return { success: false, error: validationResult.message };
      }

      addProductAndCleanup(product, qty);
      
      cartService.handleScanPauseLogic(countItems, setIsPaused, store?.item_limit);

      return { success: true };

    } catch (error) {
      console.error('Error in addToCartDirect:', error);
      return { success: false, error: error.message };
    }
  }, [
    cartItems,
    dispatch,
    addToCart,
    axiosInstance,
    setProduct,
    setCode,
    setIsPaused,
    countItems,
    store,
    addProductAndCleanup
  ]);

  /**
   * Function to handle behavior after adding (e.g. loading update)
   */
  const handleAddToCartComplete = useCallback(() => {
    if (typeof setLoading === 'function') {
      setLoading(false);
    }
  }, [setLoading]);

  return {
    addToCartWithAnimation,
    addToCartDirect,
    handleAddToCartComplete
  };
}; 